import {
	Box,
	Button,
	Card,
	Checkbox,
	Chip,
	CircularProgress,
	Container,
	FormControl,
	FormControlLabel,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	Tab,
	Tabs,
	TextField,
	Typography,
} from "@mui/material";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import BulletList from "@tiptap/extension-bullet-list";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import ListItem from "@tiptap/extension-list-item";
import TextStyle from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import StarterKit from "@tiptap/starter-kit";
import moment from "moment";
import {
	MenuButtonBold,
	MenuButtonBulletedList,
	MenuButtonEditLink,
	MenuButtonHorizontalRule,
	MenuButtonImageUpload,
	MenuButtonItalic,
	MenuButtonOrderedList,
	MenuButtonRedo,
	MenuButtonUnderline,
	MenuButtonUndo,
	MenuControlsContainer,
	MenuDivider,
	MenuSelectHeading,
	RichTextEditor,
} from "mui-tiptap";
import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useSearchParams } from "react-router-dom";
import InboxUserSearch from "src/components/inbox-table/InboxUserSearch";
import { createContentImageInContainer } from "src/utils/azureBlob";
import { get, post, put } from "src/utils/networkUtils";
import Iconify from "../components/iconify";
import { env } from "../config/env";

const dateFormat = "YYYY-MM-DD HH:mm:ss";

const DEFAULT = {
	subject: "",
	body: "",
	subjectI18n: { en: "", fr: "" },
	bodyI18n: { en: "", fr: "" },
	publishAt: moment().format(dateFormat),
	auditory: "all",
	specificUserIds: "",
	status: 0,
	userLevels: [],
};
export default function AdminInboxMessagePage() {
	const rteRef = useRef(null);
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const [message, setMessage] = useState([]);
	const [auditories, setAuditories] = useState(null);
	const [auditorySize, setAuditorySize] = useState(null);
	const [searchParams] = useSearchParams();
	const [currentTab, setCurrentTab] = useState(0);

	useEffect(() => {
		if (searchParams.has("message_id")) {
			const messageId = searchParams.get("message_id");
			get(`/admin/inbox-messages/${messageId}`).then((response) => {
				const messageData = response.adminInboxMessage;
				// Initialize i18n structure if it doesn't exist
				if (!messageData.subjectI18n) {
					messageData.subjectI18n = {
						en: messageData.subject || "",
						fr: "",
					};
				}
				if (!messageData.bodyI18n) {
					messageData.bodyI18n = {
						en: messageData.body || "",
						fr: "",
					};
				}
				setMessage(messageData);
				setLoading(false);
			});
		} else {
			setMessage(DEFAULT);
			setLoading(false);
		}

		get(`admin/inbox-messages/auditories`).then((response) => {
			console.log(response);
			setAuditories(response.auditories);
			const all = response.auditories.find((a) => a.value === "all");
			setAuditorySize(all.size);
		});
	}, [searchParams]);

	const handleI18nChange = (language, field, value) => {
		setMessage((prevState) => ({
			...prevState,
			[`${field}I18n`]: {
				...prevState[`${field}I18n`],
				[language]: value,
			},
			// Also update the main field with the English value for backward compatibility
			[field]: language === "en" ? value : prevState[field],
		}));
	};

	const handleUserLevelChange = (userValueLevelId, checked) => {
		setMessage((prevState) => {
			const currentUserLevels = prevState.userLevels || [];
			let newUserLevels;

			if (checked) {
				// Add the userValueLevelId if it's not already in the array
				newUserLevels = currentUserLevels.includes(userValueLevelId)
					? currentUserLevels
					: [...currentUserLevels, userValueLevelId.toString()];
			} else {
				// Remove the userValueLevelId from the array
				newUserLevels = currentUserLevels.filter((id) => id !== userValueLevelId.toString());
			}

			// Calculate the total user count for all checked levels
			const totalUserCount = newUserLevels.reduce((sum, levelId) => {
				const level = userLevelStats.find((l) => l.userValueLevelId.toString() === levelId);
				return sum + (level ? level.userCount : 0);
			}, 0);

			// Update auditorySize to reflect the new total
			setAuditorySize(totalUserCount);

			return {
				...prevState,
				userLevels: newUserLevels,
				size: totalUserCount,
			};
		});
	};

	const handleTabChange = (event, newValue) => {
		setCurrentTab(newValue);
	};
	const handleUserIdsChange = (userids) => {
		setAuditorySize(userids.length);
		setMessage((prevState) => ({
			...prevState,
			specificUserIds: userids.join(","),
		}));
	};

	const handleDateChange = (newValue, fieldName) => {
		setMessage((prevState) => ({
			...prevState,
			[fieldName]: moment(newValue).format(dateFormat),
		}));
	};
	const handleChangeAuditory = (event) => {
		setMessage((prevState) => ({
			...prevState,
			auditory: event.target.value,
		}));
		const auditory = auditories.find((auditory) => auditory.value === event.target.value);
		if (event.target.value === "player_level") {
			setAuditorySize(0);
			setMessage((prevState) => ({
				...prevState,
				userLevels: [],
			}));
		} else {
			setAuditorySize(auditory.size);
		}
	};
	const handleChangeStatus = (event) => {
		setMessage((prevState) => ({
			...prevState,
			status: event.target.value,
		}));
	};
	const saveHandler = () => {
		setLoading(true);

		// Prepare the message data with i18n structure
		const messageToSave = {
			...message,
			// Ensure subjectI18n and bodyI18n are properly structured
			subjectI18n: message.subjectI18n || { en: message.subject || "", fr: "" },
			bodyI18n: message.bodyI18n || { en: message.body || "", fr: "" },
		};

		if (searchParams.has("message_id")) {
			const messageId = searchParams.get("message_id");
			put(`/admin/inbox-messages/${messageId}`, messageToSave).then((response) => {
				setLoading(false);
				navigate("/dashboard/inbox");
			});
		} else {
			post(`/admin/inbox-messages`, messageToSave).then((response) => {
				setLoading(false);
				navigate("/dashboard/inbox");
			});
		}
	};
	const handleAddImage = async () => {
		const input = document.createElement("input");
		input.type = "file";
		input.accept = "image/*";
		input.onchange = async (event) => {
			const file = event.target.files[0];
			if (file) {
				const fileExtension = file.name.split(".").pop();

				const uniqueFilename = `${Date.now()}.${fileExtension}`;

				console.log("Generated Filename:", uniqueFilename);

				createContentImageInContainer(file, uniqueFilename)
					.then(() => {
						const imageUrl = `https://${env.CONTENTIMAGES_STORAGERESOURCENAME}.blob.core.windows.net/${env.CONTENTIMAGES_STORAGE_CONTAINER}/${uniqueFilename}`;
						rteRef.current.editor.chain().focus().setImage({ src: imageUrl }).run();
					})
					.catch((error) => {
						console.error("Error uploading image:", error);
					});
			}
		};
		input.click();
	};
	console.log("message", message);
	return (
		<>
			<Helmet>
				<title> Inbox Message </title>
			</Helmet>

			<Container>
				<Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
					<Typography variant="h4" gutterBottom>
						Inbox - Create New Message
					</Typography>
					<Box>
						<Button
							variant="outlined"
							onClick={() => {
								navigate("/dashboard/inbox");
							}}
							startIcon={<Iconify icon="eva:close-fill" />}
							sx={{ mr: 1 }}
						>
							Cancel
						</Button>
						<Button
							variant="contained"
							onClick={() => saveHandler()}
							startIcon={<Iconify icon="eva:save-fill" />}
							sx={{ mr: 1 }}
							disabled={
								loading ||
								(message && message.subjectI18n && message.subjectI18n.en && message.subjectI18n.en.length < 4) ||
								(message && message.bodyI18n && message.bodyI18n.en && message.bodyI18n.en.length < 4) ||
								(message && message.auditory === "userids" && (!message.specificUserIds || message.specificUserIds.length < 1))
							}
						>
							Save
						</Button>
					</Box>
				</Stack>

				<Card sx={{ minHeight: 600 }}>
					{message === null || loading ? (
						<Box sx={{ display: "flex", justifyContent: "center" }} style={{ padding: 20 }}>
							<CircularProgress />
						</Box>
					) : (
						<Grid container spacing={2} sx={{ p: 3 }}>
							<Grid item xs={3}>
								<FormControl fullWidth>
									<InputLabel id="type-select-label">Status</InputLabel>
									<Select labelId="type-label" id="type-select" value={message.status} label="Status" onChange={handleChangeStatus}>
										<MenuItem value="0">Draft</MenuItem>
										<MenuItem value="1">Published</MenuItem>
										<MenuItem value="2">Sent</MenuItem>
									</Select>
								</FormControl>
							</Grid>
							<Grid item xs={3}>
								<FormControl fullWidth>
									<InputLabel id="type-select-label">To:</InputLabel>
									<Select labelId="type-label" id="type-select" value={message.auditory} label="Type" onChange={handleChangeAuditory}>
										{auditories &&
											auditories.map((auditory) => (
												<MenuItem key={auditory.id} value={auditory.value}>
													{auditory.name}
												</MenuItem>
											))}
									</Select>
								</FormControl>
							</Grid>
							<Grid item xs={4} sx={{ m: 0, p: 1 }}>
								<LocalizationProvider dateAdapter={AdapterMoment}>
									<DateTimePicker
										label="Publish At"
										value={moment(message.publishAt, dateFormat)}
										onChange={(value) => handleDateChange(value, "publishAt")}
										renderInput={(params) => <TextField {...params} />}
									/>
								</LocalizationProvider>
							</Grid>
							<Grid item xs={2} sx={{ m: 0, p: 1 }}>
								{auditorySize !== null && (
									<Typography variant="body1" sx={{ mt: 2 }}>
										{`Group size: ${auditorySize}`}
									</Typography>
								)}
							</Grid>
							{message.auditory === "userids" && (
								<Grid item xs={12} sx={{ m: 0, p: 1 }}>
									<InboxUserSearch onUserSelect={handleUserIdsChange} users={message.users} />
								</Grid>
							)}
							{message.auditory === "player_level" && userLevelStats.length > 0 && (
								<Grid item xs={12}>
									<Typography variant="h6" sx={{ mt: 2 }}>
										User Levels:
									</Typography>
									<Grid container spacing={2}>
										{userLevelStats.map((level) => (
											<Grid item xs={2} key={level.id}>
												<FormControlLabel
													control={
														<Checkbox
															checked={message.userLevels?.includes(level.userValueLevelId.toString())}
															onChange={(e) => handleUserLevelChange(level.userValueLevelId, e.target.checked)}
														/>
													}
													label={
														<Chip
															label={level.name}
															sx={{
																backgroundColor: level.color || "#ccc",
																minWidth: 70,
															}}
														/>
													}
												/>
											</Grid>
										))}
									</Grid>
								</Grid>
							)}

							{/* Language Tabs */}
							<Grid item xs={12}>
								<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
									<Tabs value={currentTab} onChange={handleTabChange} aria-label="language tabs">
										<Tab label="English" />
										<Tab label="Français" />
									</Tabs>
								</Box>
							</Grid>

							{/* English Tab Content */}
							{currentTab === 0 && (
								<>
									<Grid item xs={12}>
										<TextField
											id="subject-en"
											label="Subject (English)"
											fullWidth
											value={message.subjectI18n?.en || ""}
											variant="standard"
											onChange={(e) => handleI18nChange("en", "subject", e.target.value)}
										/>
									</Grid>
									<Grid item xs={12}>
										<RichTextEditor
											ref={rteRef}
											extensions={[
												StarterKit,
												TextStyle,
												Underline,
												BulletList,
												ListItem,
												Link.configure({
													openOnClick: true,
													autolink: true,
												}),
												Image.configure({
													inline: true,
												}),
											]}
											content={message.bodyI18n?.en || ""}
											onUpdate={({ editor }) => {
												const html = editor.getHTML();
												handleI18nChange("en", "body", html);
											}}
											renderControls={() => (
												<MenuControlsContainer>
													<MenuSelectHeading />
													<MenuDivider />
													<MenuButtonBold />
													<MenuButtonItalic />
													<MenuButtonUnderline />
													<MenuButtonOrderedList />
													<MenuButtonBulletedList />
													<MenuButtonUndo />
													<MenuButtonRedo />
													<MenuButtonHorizontalRule />
													<MenuDivider />

													<MenuButtonEditLink
														onClick={() => {
															const url = prompt("Enter the URL:");
															if (url) {
																rteRef.current.editor.chain().focus().setLink({ href: url }).run();
															}
														}}
													/>
													<MenuButtonImageUpload onClick={handleAddImage} variant="outlined" size="small" sx={{ ml: 1 }} />
												</MenuControlsContainer>
											)}
										/>
									</Grid>
								</>
							)}

							{/* French Tab Content */}
							{currentTab === 1 && (
								<>
									<Grid item xs={12}>
										<TextField
											id="subject-fr"
											label="Subject (Français)"
											fullWidth
											value={message.subjectI18n?.fr || ""}
											variant="standard"
											onChange={(e) => handleI18nChange("fr", "subject", e.target.value)}
										/>
									</Grid>
									<Grid item xs={12}>
										<RichTextEditor
											key="fr-editor" // Force re-render when switching tabs
											extensions={[
												StarterKit,
												TextStyle,
												Underline,
												BulletList,
												ListItem,
												Link.configure({
													openOnClick: true,
													autolink: true,
												}),
												Image.configure({
													inline: true,
												}),
											]}
											content={message.bodyI18n?.fr || ""}
											onUpdate={({ editor }) => {
												const html = editor.getHTML();
												handleI18nChange("fr", "body", html);
											}}
											renderControls={() => (
												<MenuControlsContainer>
													<MenuSelectHeading />
													<MenuDivider />
													<MenuButtonBold />
													<MenuButtonItalic />
													<MenuButtonUnderline />
													<MenuButtonOrderedList />
													<MenuButtonBulletedList />
													<MenuButtonUndo />
													<MenuButtonRedo />
													<MenuButtonHorizontalRule />
													<MenuDivider />

													<MenuButtonEditLink
														onClick={() => {
															const url = prompt("Enter the URL:");
															if (url) {
																rteRef.current.editor.chain().focus().setLink({ href: url }).run();
															}
														}}
													/>
													<MenuButtonImageUpload onClick={handleAddImage} variant="outlined" size="small" sx={{ ml: 1 }} />
												</MenuControlsContainer>
											)}
										/>
									</Grid>
								</>
							)}
						</Grid>
					)}
				</Card>
			</Container>
		</>
	);
}
